import type React from 'react'

export function navigateToCommentsSplitDiff(
  e: React.KeyboardEvent,
  {
    hasLeftThreads,
    hasRightThreads,
    hasRightStoredComment,
    hasLeftStoredComment,
    enableDialogMode,
    leftCodeCellId,
    rightCodeCellId,
    setIsLeftNewConversationDialogOpen,
    setIsRightNewConversationDialogOpen,
  }: {
    hasLeftThreads: boolean
    hasRightThreads: boolean
    hasRightStoredComment: boolean | undefined
    hasLeftStoredComment: boolean | undefined
    enableDialogMode: (cellId: string) => void
    leftCodeCellId: string
    rightCodeCellId: string
    setIsLeftNewConversationDialogOpen: (open: boolean) => void
    setIsRightNewConversationDialogOpen: (open: boolean) => void
  },
) {
  if (!isEnterkey(e) || !isCell(e)) return

  e.preventDefault()

  const diffSide = e.currentTarget.getAttribute('data-diff-side')

  if (diffSide === 'left') {
    if (hasLeftStoredComment) {
      enableDialogMode(leftCodeCellId)
      focusOnPersistedNewCommentBox(e)
      return
    }

    if (hasLeftThreads) {
      enableDialogMode(leftCodeCellId)
      focusOnFirstExistingThread(e)
      return
    }

    setIsLeftNewConversationDialogOpen(true)
  } else if (diffSide === 'right') {
    if (hasRightStoredComment) {
      enableDialogMode(rightCodeCellId)
      focusOnPersistedNewCommentBox(e)
      return
    }

    if (hasRightThreads) {
      enableDialogMode(rightCodeCellId)
      focusOnFirstExistingThread(e)
      return
    }
    setIsRightNewConversationDialogOpen(true)
  }
}

export function navigateToCommentsUnifiedDiff(
  e: React.KeyboardEvent,
  {
    hasThreads,
    hasStoredComment,
    enableDialogMode,
    codeCellId,
    setIsNewConversationDialogOpen,
  }: {
    hasThreads: boolean
    hasStoredComment: boolean | undefined
    enableDialogMode: (cellId: string) => void
    codeCellId: string
    setIsNewConversationDialogOpen: (open: boolean) => void
  },
) {
  if (!isEnterkey(e) || !isCell(e)) return

  e.preventDefault()

  if (hasStoredComment) {
    enableDialogMode(codeCellId)
    focusOnPersistedNewCommentBox(e)
    return
  }

  if (hasThreads) {
    enableDialogMode(codeCellId)
    focusOnFirstExistingThread(e)
    return
  }

  setIsNewConversationDialogOpen(true)
}

// eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
const isEnterkey = (e: React.KeyboardEvent) => e.key === 'Enter'

const isCell = (e: React.KeyboardEvent) => e.currentTarget.nodeName === 'TD'

const focusOnFirstExistingThread = (e: React.KeyboardEvent) => {
  const contentCell = findCodeCell(e)

  // Attempt to grab the first thread marker element
  const firstThreadMarker = contentCell?.querySelector<HTMLElement>('[data-first-marker="true"]')
  // If there is not a thread marker, look for the first data-marker-id (e.g. Annotation)
  const firstMarker = firstThreadMarker ?? contentCell?.querySelector<HTMLElement>('[data-marker-id]')
  firstMarker?.focus()
}

const focusOnPersistedNewCommentBox = (e: React.KeyboardEvent) => {
  const contentCell = findCodeCell(e)

  // Focus on the new comment box.
  contentCell?.querySelector<HTMLElement>('[data-marker-id="new-comment"]')?.querySelector('textarea')?.focus()
}

const findCodeCell = (e: React.KeyboardEvent) => {
  // Use existing target if not a line number cell, otherwise use the sibling element.
  let contentCell = e.currentTarget.classList.contains('new-diff-line-number')
    ? e.currentTarget.nextElementSibling
    : e.currentTarget

  // Use contentCell if not a line number cell, otherwise use the next sibling element
  // This will only ever be true for Unified diff
  if (contentCell?.classList.contains('new-diff-line-number')) {
    contentCell = contentCell?.nextElementSibling
  }

  return contentCell
}
